import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import CreditPill from "@/components/CreditPill";
import { useCredits } from "@/context/CreditsContext";
import { useAuth } from "@/context/AuthContext";
import NarrationBar from "@/components/NarrationBar";
import AudiobookDialog from "@/components/AudiobookDialog";
import ElevenReaderDialog from "@/components/ElevenReaderDialog";
import { AUDIOBOOKS_ENABLED } from "@/config/features";
import DraftGeneratorDialog from "@/components/DraftGeneratorDialog";
import RewriteChapterDialog from "@/components/RewriteChapterDialog";
import PolishChapterDialog from "@/components/PolishChapterDialog";
import StyleBriefDialog from "@/components/StyleBriefDialog";
import { gorePassChapter, tidyChapter } from "@/lib/rewriteChapter";
import TidyBookDialog from "@/components/TidyBookDialog";
import ContinuityDialog from "@/components/ContinuityDialog";
import DedupeDialog from "@/components/DedupeDialog";
import RichEditor from "@/components/RichEditor";
import BranchMap from "@/components/BranchMap";
import GearSyncDialog from "@/components/GearSyncDialog";
import DetectReviewDialog from "@/components/DetectReviewDialog";
import CodexGrid from "@/components/CodexGrid";
import BlueprintBoard from "@/components/BlueprintBoard";
import ManageTypesDialog from "@/components/ManageTypesDialog";
import BeatPlanner from "@/components/BeatPlanner";
import InventoryView from "@/components/InventoryView";
import EntityDialog from "@/components/EntityDialog";
import ImportDialog from "@/components/ImportDialog";
import ChoicesEditor from "@/components/ChoicesEditor";
import { highlightHtml } from "@/lib/highlight";
import { saveDraft, getDraft, clearDraft, allDraftIds } from "@/lib/drafts";
import { slugType } from "@/lib/entities";
import { numberedTitles } from "@/lib/chapters";
import useProjectSync from "@/lib/useProjectSync";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Sparkles, PenSquare, Library, Highlighter, SpellCheck2, Download, Upload, Feather,
  Loader2, Check, Trash2, Edit3, ChevronUp, ChevronDown, FileText, BookOpen, Eye, LayoutList, ListChecks, Boxes, CheckCheck, Waypoints, SlidersHorizontal, Menu, RefreshCw, UserRound, History, RotateCcw, FilePlus2, Settings2, BookmarkPlus, X, ArrowUpToLine, ArrowDownToLine, ExternalLink, Eraser, Headphones, Clock, Wand2, ShieldCheck, CopyCheck, Skull, Image as ImageIcon,
} from "lucide-react";
import AutocorrectDialog from "@/components/AutocorrectDialog";
import WhatsNextDialog from "@/components/WhatsNextDialog";

const SECTION_BUILTIN = [
  { title: "Prologue", placement: "front" },
  { title: "Dedication", placement: "front" },
  { title: "About the Author", placement: "back" },
  { title: "Acknowledgements", placement: "back" },
  { title: "Epilogue", placement: "back" },
];
const secSlug = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Time estimates from word count. Reading ~200 wpm; audiobook narration ~150 wpm.
const readMins = (w) => Math.max(1, Math.round((w || 0) / 200));
const listenMins = (w) => Math.max(1, Math.round((w || 0) / 150));
const fmtMins = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ""}`.trim() : `${m}m`);


const STATUS = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground" },
  review: { label: "In review", color: "bg-amber-500/20 text-amber-600 dark:text-amber-300" },
  complete: { label: "Complete", color: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-300" },
};

function htmlToText(html) {
  const d = new DOMParser().parseFromString(html || "", "text/html");
  const blocks = d.body.querySelectorAll("p,h1,h2,h3,blockquote,li,div");
  if (blocks.length === 0) return d.body.textContent || "";
  return Array.from(blocks).map((b) => b.textContent).join("\n\n");
}

function download(name, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

// Prefer a locally-backed-up draft over the server copy when it's genuinely newer
// (i.e. unsaved work from a failed save or a refresh).
function pickInitialHtml(chapter) {
  if (!chapter) return "";
  const d = getDraft(chapter.id);
  const serverTs = Date.parse(chapter.updated_at) || 0;
  if (d && d.html !== (chapter.content || "") && d.ts > serverTs) return d.html;
  return chapter.content || "";
}

export default function Studio() {
  const { projectId } = useParams();
  const { refresh: refreshCredits } = useCredits();
  const { user: authUser } = useAuth();
  const aiOn = authUser?.ai_tools_enabled !== false;
  const PANEL_VIEWS = { codex: "codex", loadouts: "inventory", blueprint: "blueprint" };
  const panelParam = new URLSearchParams(window.location.search).get("panel");
  const panelMode = !!PANEL_VIEWS[panelParam];
  const [project, setProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [entities, setEntities] = useState([]);
  // Words autocorrect must never touch (Codex names + nicknames, split into single words).
  const protectedNames = useMemo(() => {
    const s = new Set();
    entities.forEach((e) => {
      [e.name, e.nickname].forEach((v) => {
        if (!v) return;
        v.toLowerCase().split(/\s+/).forEach((w) => { const t = w.replace(/[^a-z']/g, ""); if (t) s.add(t); });
      });
    });
    return s;
  }, [entities]);
  const [inventory, setInventory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState(() => {
    if (panelMode) return PANEL_VIEWS[panelParam];
    try {
      const s = JSON.parse(localStorage.getItem(`cs_last_${projectId}`) || "null");
      if (s?.view && ["write", "codex", "inventory", "blueprint", "map"].includes(s.view)) return s.view;
    } catch { /* ignore */ }
    return "write";
  }); // write | codex | inventory | blueprint | map
  const [chaptersSheetOpen, setChaptersSheetOpen] = useState(false);
  const [spellcheck, setSpellcheck] = useState(true);
  const [highlight, setHighlight] = useState(false);
  const [stats, setStats] = useState({ words: 0, minutes: 1 });
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [scanning, setScanning] = useState(false);
  const [entityDialog, setEntityDialog] = useState({ open: false, entity: null });
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [autocorrectOpen, setAutocorrectOpen] = useState(false);
  const [whatsNextOpen, setWhatsNextOpen] = useState(false);
  const [customRules, setCustomRules] = useState({});
  const rulesToMap = (list) => Object.fromEntries((list || []).map((r) => [r.from, r.to]));
  const [audiobookOpen, setAudiobookOpen] = useState(false);
  const [elevenReaderOpen, setElevenReaderOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [polishOpen, setPolishOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [paraBusy, setParaBusy] = useState("");
  const [tidyBookOpen, setTidyBookOpen] = useState(false);
  const [continuityOpen, setContinuityOpen] = useState(false);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const saveTitle = async () => {
    const t = titleDraft.trim();
    setEditingTitle(false);
    if (t && t !== project.title) await updateProject({ title: t });
  };
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [deleteChapter, setDeleteChapter] = useState(null);
  const [beatId, setBeatId] = useState(null);
  const [beatOpen, setBeatOpen] = useState(false);
  const openBeats = (id) => { setBeatId(id); setBeatOpen(true); };
  const [proof, setProof] = useState(null); // {loading, issues}
  const [proofing, setProofing] = useState(false);

  const runProofread = async () => {
    setProofing(true);
    setProof({ loading: true, issues: [] });
    try {
      if (saveState !== "saved") await persist();
      const { data } = await api.post(`/projects/${projectId}/proofread`, { text: contentRef.current });
      refreshCredits?.();
      if (!data.issues.length) { toast.success("No issues found — looking clean!"); setProof(null); }
      else setProof({ loading: false, issues: data.issues.map((x, i) => ({ ...x, id: i, done: false })) });
    } catch (e) {
      if (!e.handledOutOfCredits) toast.error(e.response?.data?.detail || "Proofread failed");
      setProof(null);
    } finally {
      setProofing(false);
    }
  };
  const applyFix = (issue) => {
    const ok = editorRef.current?.replaceText(issue.original, issue.suggestion);
    if (ok) setProof((p) => ({ ...p, issues: p.issues.map((i) => i.id === issue.id ? { ...i, done: true } : i) }));
    else toast.error("Couldn't locate that text — it may already be edited");
    return ok;
  };
  const fixAll = () => { (proof?.issues || []).filter((i) => !i.done).forEach(applyFix); };

  const [gear, setGear] = useState(null); // {loading, chapterTitle, results}
  const [gearBusy, setGearBusy] = useState(false);
  const [me, setMe] = useState(null); // account profile: author_bio + section_presets
  const [manageSections, setManageSections] = useState(false);
  const [conflict, setConflict] = useState(null); // {mine, serverContent, serverUat}
  const [versions, setVersions] = useState(null); // null=closed, []|[...]=open
  const runGearSync = async () => {
    if (!activeChapter) return;
    setGearBusy(true);
    setGear({ loading: true, results: [] });
    try {
      if (saveState !== "saved") await persist();
      const { data } = await api.post(`/projects/${projectId}/chapters/${activeId}/sync-gear`, { apply: false });
      refreshCredits?.();
      if (data.empty) { toast.info("This section has no text to scan yet"); setGear(null); return; }
      const kiaFlags = data.kia_flags || [];
      const kiaSuggestions = data.kia_suggestions || [];
      if (!data.results.length && !kiaFlags.length && !kiaSuggestions.length) { toast.success("Up to date — no new gear changes since last sync"); setGear(null); return; }
      setGear({
        loading: false, chapterTitle: data.chapter_title, sources: data.sources || [], kiaFlags, kiaSuggestions,
        results: data.results.map((r, i) => ({ ...r, id: i, keep: r.already_synced ? true : r.confidence !== "low" })),
      });
    } catch (e) {
      if (!e.handledOutOfCredits) toast.error(e.response?.data?.detail || "Gear scan failed");
      setGear(null);
    } finally { setGearBusy(false); }
  };
  const applyGearSync = async () => {
    const kept = gear.results.filter((r) => r.keep);
    if (!kept.length) { setGear(null); return; }
    const sharedKept = kept.filter((r) => r.shared);
    const personalKept = kept.filter((r) => !r.shared);
    const applyTo = gear.applyTo || [];
    if (sharedKept.length && !applyTo.length) {
      toast.warning("Pick who receives the shared kit first");
      return;
    }
    // Personal items keep their detected owner; shared items are copied to each chosen recipient.
    const ops = [...personalKept];
    sharedKept.forEach((r) => applyTo.forEach((c) =>
      ops.push({ ...r, shared: false, owner: c.name, owner_type: "character", resolved_owner_id: c.id })));
    setGearBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${activeId}/sync-gear`, { apply: true, operations: ops });
      await Promise.all([reloadInventory(), reloadEntities()]);
      setGear(null);
      const who = sharedKept.length && applyTo.length ? ` (shared kit to ${applyTo.length} character${applyTo.length !== 1 ? "s" : ""})` : "";
      toast.success(`${ops.length} gear change${ops.length !== 1 ? "s" : ""} synced${who}`);
      (data.warnings || []).slice(0, 4).forEach((msg) => toast.warning(msg));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't apply gear changes");
    } finally { setGearBusy(false); }
  };

  const markKiaFromSuggestion = async (ownerId) => {
    try {
      await api.put(`/entities/${ownerId}`, { kia: true });
      await reloadEntities();
      setGear((g) => (g ? { ...g, kiaSuggestions: (g.kiaSuggestions || []).filter((s) => s.owner_id !== ownerId) } : g));
      toast.success("Marked KIA — now frozen, and their gear can be looted in Loadouts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark KIA");
    }
  };
  const dismissKiaSuggestion = (ownerId) =>
    setGear((g) => (g ? { ...g, kiaSuggestions: (g.kiaSuggestions || []).filter((s) => s.owner_id !== ownerId) } : g));

  const rescanGearFromScratch = async () => {
    if (!activeChapter || gearBusy) return;
    setGearBusy(true);
    try {
      if (saveState !== "saved") await persist();
      const { data } = await api.post(`/projects/${projectId}/chapters/${activeId}/sync-gear/reset`);
      await reloadInventory();
      toast.success(data.reversed ? `Cleared this chapter's ${data.reversed} synced move${data.reversed !== 1 ? "s" : ""} — re-reading from scratch` : "Re-reading this chapter from scratch");
    } catch (e) {
      if (!e.handledOutOfCredits) toast.error(e.response?.data?.detail || "Couldn't reset chapter sync");
      setGearBusy(false);
      return;
    } finally { setGearBusy(false); }
    await runGearSync();
  };

  const contentRef = useRef("");
  const saveTimer = useRef(null);
  const editorRef = useRef(null);
  const saveStateRef = useRef("saved");
  const activeIdRef = useRef(null);
  const baseUatRef = useRef(null);
  const chaptersRef = useRef([]);
  useEffect(() => { saveStateRef.current = saveState; }, [saveState]);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  useEffect(() => { chaptersRef.current = chapters; }, [chapters]);

  const activeChapter = chapters.find((c) => c.id === activeId);
  const chapterTitles = useMemo(() => numberedTitles(chapters), [chapters]);

  const loadAll = useCallback(async () => {
    const [p, ch, en, inv] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/chapters`),
      api.get(`/projects/${projectId}/entities`),
      api.get(`/projects/${projectId}/inventory`),
    ]);
    setProject(p.data);
    setChapters(ch.data);
    setEntities(en.data);
    setInventory(inv.data);
    setActiveId((prev) => {
      if (prev && ch.data.some((c) => c.id === prev)) return prev;
      // First load: reopen the chapter the writer left off on (per book), else the first.
      try {
        const s = JSON.parse(localStorage.getItem(`cs_last_${projectId}`) || "null");
        if (s?.chapterId && ch.data.some((c) => c.id === s.chapterId)) return s.chapterId;
      } catch { /* ignore */ }
      return ch.data[0]?.id || null;
    });
  }, [projectId]);

  const reloadEntities = async () => {
    const { data } = await api.get(`/projects/${projectId}/entities`);
    setEntities(data);
  };
  const [suggestingVoices, setSuggestingVoices] = useState(false);
  const suggestVoices = async () => {
    setSuggestingVoices(true);
    try {
      await persist();
      const { data } = await api.post(`/projects/${projectId}/entities/suggest-voices`);
      if (data.updated?.length) {
        toast.success(`Added voice notes for ${data.updated.map((u) => u.name).join(", ")}. Open a character to edit theirs.`);
        await reloadEntities();
        refreshCredits?.();
      } else toast.info(data.message || "No new voice notes were needed.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't suggest voices — please try again"));
    } finally { setSuggestingVoices(false); }
  };
  const reloadInventory = async () => {
    const { data } = await api.get(`/projects/${projectId}/inventory`);
    setInventory(data);
  };

  // --- Cross-window sync (undockable panels) ---
  const remoteApplying = useRef(false);
  const applyRemote = useCallback(async () => {
    remoteApplying.current = true;
    try {
      const [p, en, inv] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/entities`),
        api.get(`/projects/${projectId}/inventory`),
      ]);
      setProject(p.data); setEntities(en.data); setInventory(inv.data);
      // Only refresh chapters when NOT mid-edit, so we never clobber unsaved typing.
      if (saveStateRef.current === "saved") {
        const ch = await api.get(`/projects/${projectId}/chapters`);
        setChapters(ch.data);
      }
    } catch { /* ignore transient */ }
    setTimeout(() => { remoteApplying.current = false; }, 400);
  }, [projectId]);
  const pingSync = useProjectSync(projectId, applyRemote);
  useEffect(() => {
    if (remoteApplying.current || saveStateRef.current !== "saved") return undefined;
    const t = setTimeout(() => pingSync(), 300);
    return () => clearTimeout(t);
  }, [entities, inventory, project, chapters, pingSync]);
  useEffect(() => {
    const onFocus = () => applyRemote();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [applyRemote]);

  // Undock a panel into its own resizable window (remembers its last size).
  const undock = (which) => {
    const key = `cs_panel_${which}`;
    let size = { w: 1100, h: 900 };
    try { const s = JSON.parse(localStorage.getItem(key) || "null"); if (s?.w) size = s; } catch { /* ignore */ }
    const left = window.screenX + 60, top = window.screenY + 60;
    window.open(`/project/${projectId}?panel=${which}`, `cs-${which}-${projectId}`,
      `width=${size.w},height=${size.h},left=${left},top=${top},menubar=no,toolbar=no,location=no`);
  };
  // In a panel window, remember its size so it reopens the same.
  useEffect(() => {
    if (!panelMode) return undefined;
    const key = `cs_panel_${panelParam}`;
    const save = () => { try { localStorage.setItem(key, JSON.stringify({ w: window.outerWidth, h: window.outerHeight })); } catch { /* ignore */ } };
    window.addEventListener("resize", save);
    return () => window.removeEventListener("resize", save);
  }, [panelMode, panelParam]);
  const currentPanelKey = view === "codex" ? "codex" : view === "inventory" ? "loadouts" : view === "blueprint" ? "blueprint" : null;
  const insertToEditor = (text) => editorRef.current?.insertText(text);

  useEffect(() => { loadAll(); }, [loadAll]);
  // Remember where the writer left off per book (last chapter + view), so reopening resumes there.
  useEffect(() => {
    if (panelMode || !projectId || !activeId) return;
    try { localStorage.setItem(`cs_last_${projectId}`, JSON.stringify({ chapterId: activeId, view })); } catch { /* ignore */ }
  }, [projectId, activeId, view, panelMode]);
  useEffect(() => { api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/autocorrect").then(({ data }) => setCustomRules(rulesToMap(data.rules))).catch(() => {}); }, []);

  useEffect(() => {
    if (!activeChapter) return;
    const d = getDraft(activeChapter.id);
    const serverTs = Date.parse(activeChapter.updated_at) || 0;
    if (d && d.html !== (activeChapter.content || "") && d.ts > serverTs) {
      // Unsaved work recovered from this device — show it and sync it back.
      contentRef.current = d.html;
      setSaveState("dirty");
      toast.info("Restored unsaved changes from this device");
      setTimeout(() => flushDrafts(), 200);
    } else {
      if (d) clearDraft(activeChapter.id);
      contentRef.current = activeChapter.content || "";
    }
    baseUatRef.current = activeChapter.updated_at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const saving = useRef(false);
  const persist = useCallback(async (extra = {}) => {
    if (!activeId) return;
    saveDraft(activeId, contentRef.current); // local safety copy first
    if (saving.current) {
      // A save (retry or background flush) is already running — retry shortly so the two
      // never race and leave baseUatRef stale (which caused false "changed elsewhere" 409s).
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(extra), 300);
      return;
    }
    saving.current = true;
    setSaveState("saving");
    try {
      const { data } = await api.put(`/chapters/${activeId}`,
        { content: contentRef.current, base_updated_at: baseUatRef.current, ...extra });
      setChapters((cs) => cs.map((c) => (c.id === activeId ? { ...c, ...data } : c)));
      baseUatRef.current = data.updated_at;
      clearDraft(activeId);
      setSaveState("saved");
    } catch (e) {
      if (e.response?.status === 409) {
        // Another device saved newer work. Don't clobber — let the author choose.
        const d = e.response.data?.detail || {};
        setConflict({ mine: contentRef.current, serverContent: d.server_content || "", serverUat: d.server_updated_at });
        setSaveState("dirty");
        return;
      }
      // Keep the local draft; the background flusher will keep retrying.
      setSaveState("retrying");
    } finally {
      saving.current = false;
    }
  }, [activeId]);

  // Background auto-retry: pushes any locally-held drafts (for ANY chapter) to the
  // server every few seconds, so a sleeping backend self-heals once it wakes.
  const flushDrafts = useCallback(async () => {
    if (saving.current) return;
    // Don't push from a backgrounded / screen-locked / sleeping tab — a stale draft from a
    // previous session must never overwrite work done since on another device.
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const ids = allDraftIds();
    if (!ids.length) return;
    saving.current = true;
    let anyFail = false;
    try {
      for (const id of ids) {
        const d = getDraft(id);
        if (!d) continue;
        // Version-aware: send the baseline this draft was built on so the server can REJECT a
        // stale overwrite (409) instead of clobbering newer content saved elsewhere.
        const base = id === activeIdRef.current
          ? baseUatRef.current
          : (chaptersRef.current.find((c) => c.id === id)?.updated_at || null);
        try {
          const { data } = await api.put(`/chapters/${id}`, { content: d.html, base_updated_at: base }, { _retryDisabled: true });
          setChapters((cs) => cs.map((c) => (c.id === id ? { ...c, ...data } : c)));
          // Keep the save-guard baseline in sync for the chapter we're editing.
          if (id === activeIdRef.current) baseUatRef.current = data.updated_at;
          const cur = getDraft(id);
          if (cur && cur.ts === d.ts) clearDraft(id); // only clear if not edited meanwhile
        } catch (e) {
          if (e.response?.status === 409) {
            // Server already holds a newer version than this draft. Never blindly overwrite.
            if (id === activeIdRef.current) {
              // Live conflict on the chapter being edited — surface the chooser (don't get
              // stuck on "Reconnecting"): the author picks Load-newer / Keep-mine.
              const dd = e.response.data?.detail || {};
              setConflict({ mine: d.html, serverContent: dd.server_content || "", serverUat: dd.server_updated_at });
              setSaveState("dirty");
            } else {
              // A stale/background draft — drop it so we stop clobbering & retrying.
              clearDraft(id);
            }
          } else {
            anyFail = true;
          }
        }
      }
    } finally {
      // ALWAYS release the lock, even on an unexpected error — otherwise every future save
      // is skipped and the app looks stuck on "Reconnecting" until a manual page refresh.
      saving.current = false;
    }
    if (!allDraftIds().length) setSaveState((s) => (s === "retrying" ? "saved" : s));
    else if (anyFail) setSaveState((s) => (s === "dirty" ? s : "retrying"));
  }, []);

  useEffect(() => {
    const t = setInterval(() => flushDrafts(), 5000);
    return () => clearInterval(t);
  }, [flushDrafts]);

  // Heal immediately when the tab wakes / regains focus / the network returns — so a stuck
  // "Reconnecting" resolves on its own without the author having to refresh the page.
  useEffect(() => {
    const heal = () => { if (document.visibilityState === "visible") flushDrafts(); };
    window.addEventListener("online", heal);
    window.addEventListener("focus", heal);
    document.addEventListener("visibilitychange", heal);
    return () => {
      window.removeEventListener("online", heal);
      window.removeEventListener("focus", heal);
      document.removeEventListener("visibilitychange", heal);
    };
  }, [flushDrafts]);

  useEffect(() => {
    const warn = (e) => { if (allDraftIds().length) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  // Multi-device auto-refresh: while this tab is on screen (and again the moment it
  // regains focus), pull the latest from the server so every device stays current.
  // Safety rule: never overwrite the editor while there are unsaved edits here.
  const syncFromServer = useCallback(async () => {
    if (!projectId || saveStateRef.current !== "saved") return;
    try {
      const [ch, en, inv] = await Promise.all([
        api.get(`/projects/${projectId}/chapters`),
        api.get(`/projects/${projectId}/entities`),
        api.get(`/projects/${projectId}/inventory`),
      ]);
      const aid = activeIdRef.current;
      const serverActive = ch.data.find((c) => c.id === aid);
      if (serverActive && saveStateRef.current === "saved") {
        // NEVER rewrite the editor while the cursor is inside it — that resets the caret to
        // the top (the mobile "jumps to the start while typing" bug). Only apply an external
        // update when the editor isn't focused; otherwise leave local content as-is and let a
        // later sync (after blur) catch up. The save-guard still protects genuine conflicts.
        const editorFocused = !!editorRef.current?.isFocused?.();
        if (!editorFocused && (serverActive.content || "") !== contentRef.current) {
          contentRef.current = serverActive.content || "";
          editorRef.current?.setHtml(serverActive.content || "");
          clearDraft(aid);
          toast.info("Updated with newer changes from another device");
        }
        if (!editorFocused) baseUatRef.current = serverActive.updated_at;
      }
      // Refresh the sidebar/codex/loadouts, but keep the active chapter's local copy
      // untouched if we're mid-edit (so we never clobber unsaved work on remount).
      setChapters((prev) => ch.data.map((sc) => {
        if (sc.id === aid && saveStateRef.current !== "saved") {
          return prev.find((c) => c.id === aid) || sc;
        }
        return sc;
      }));
      setEntities(en.data);
      setInventory(inv.data);
    } catch { /* transient network/backend blip — try again next tick */ }
  }, [projectId]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") syncFromServer(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", syncFromServer);
    const t = setInterval(() => {
      if (document.visibilityState === "visible") syncFromServer();
    }, 25000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", syncFromServer);
      clearInterval(t);
    };
  }, [syncFromServer]);

  const onEditorChange = (html) => {
    contentRef.current = html;
    saveDraft(activeId, html);
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(), 1200);
  };

  const switchChapter = async (id) => {
    if (id === activeId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (saveState !== "saved") await persist();
    setHighlight(false);
    setActiveId(id);
  };

  const addChapter = async () => {
    const { data } = await api.post(`/projects/${projectId}/chapters`, {
      title: `Chapter ${chapters.length + 1}`, content: "<p></p>",
    });
    setChapters((cs) => [...cs, data]);
    setActiveId(data.id);
    toast.success("Chapter added");
  };

  const bookSections = (project?.section_presets) || (me?.section_presets) || SECTION_BUILTIN;

  const addSection = async (preset) => {
    const isBio = (preset.title || "").trim().toLowerCase() === "about the author";
    let content = "<p></p>";
    if (isBio && (me?.author_bio || "").trim()) content = me.author_bio;
    const orders = chapters.map((c) => c.order ?? 0);
    const order = preset.placement === "front"
      ? (orders.length ? Math.min(...orders) - 1 : 0)
      : (orders.length ? Math.max(...orders) + 1 : 0);
    const { data } = await api.post(`/projects/${projectId}/chapters`, { title: preset.title, content, order });
    setChapters((cs) => [...cs, data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setActiveId(data.id);
    setChaptersSheetOpen(false);
    toast.success(isBio && content !== "<p></p>" ? `${preset.title} added — filled from your saved bio` : `${preset.title} added`);
  };

  const saveAuthorBio = async () => {
    try {
      if (saveState !== "saved") await persist();
      const { data } = await api.put("/auth/profile", { author_bio: contentRef.current });
      setMe((m) => ({ ...(m || {}), author_bio: data.author_bio }));
      toast.success("Saved as your Author Bio — it'll prefill in your other books");
    } catch { toast.error("Couldn't save your bio"); }
  };

  const saveBookSections = async (list) => {
    await updateProject({ section_presets: list });
    toast.success("Section list saved for this book");
  };
  const saveDefaultSections = async (list) => {
    const { data } = await api.put("/auth/profile", { section_presets: list });
    setMe((m) => ({ ...(m || {}), section_presets: data.section_presets }));
    toast.success("Saved as your default — new books will start with these");
  };

  const saveRename = async () => {
    if (renameId && renameVal.trim()) {
      const { data } = await api.put(`/chapters/${renameId}`, { title: renameVal.trim() });
      setChapters((cs) => cs.map((c) => (c.id === renameId ? { ...c, title: data.title } : c)));
    }
    setRenameId(null);
  };

  const setStatus = async (id, status) => {
    const { data } = await api.put(`/chapters/${id}`, { status });
    setChapters((cs) => cs.map((c) => (c.id === id ? { ...c, status: data.status } : c)));
  };

  const removeChapter = async () => {
    const id = deleteChapter.id;
    await api.delete(`/chapters/${id}`);
    const remaining = chapters.filter((c) => c.id !== id);
    setChapters(remaining);
    if (activeId === id) setActiveId(remaining[0]?.id || null);
    setDeleteChapter(null);
    toast.success("Chapter deleted");
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= chapters.length) return;
    const next = [...chapters];
    [next[index], next[target]] = [next[target], next[index]];
    setChapters(next);
    await api.post(`/projects/${projectId}/chapters/reorder`, next.map((c) => c.id));
  };

  // ---- Blueprint handlers ----
  const scrollPos = useRef({}); // remember manuscript scroll per chapter
  const changeView = async (v) => {
    // Remember where we were in the manuscript so returning lands in the same spot.
    if (view === "write" && activeId && editorRef.current?.getScroll) {
      scrollPos.current[activeId] = editorRef.current.getScroll();
    }
    if (v !== "write" && saveState !== "saved") await persist();
    setView(v);
  };
  const updateProject = async (patch) => {
    const { data } = await api.put(`/projects/${projectId}`, patch);
    setProject(data);
  };
  const addCustomType = async ({ label, color, is_owner }) => {
    const key = slugType(label);
    const list = project.custom_types || [];
    if (!list.some((t) => t.key === key)) {
      await updateProject({ custom_types: [...list, { key, label, color, is_owner }] });
    }
    return key;
  };
  const updateChapterMeta = async (id, patch) => {
    const { data } = await api.put(`/chapters/${id}`, patch);
    setChapters((cs) => cs.map((c) => (c.id === id ? { ...c, ...data } : c)));
  };
  const addChapterToAct = async (actId, extra = {}) => {
    const { data } = await api.post(`/projects/${projectId}/chapters`, {
      title: extra.title || `Chapter ${chapters.length + 1}`,
      content: "<p></p>", act_id: actId, summary: extra.summary || "",
    });
    setChapters((cs) => [...cs, data]);
    return data;
  };
  const createSection = (title, actId) => addChapterToAct(actId, { title });
  const reorderChapters = async (idList) => {
    setChapters((cs) => {
      const map = Object.fromEntries(cs.map((c) => [c.id, c]));
      return idList.map((id, i) => ({ ...map[id], order: i }));
    });
    await api.post(`/projects/${projectId}/chapters/reorder`, idList);
  };
  const openChapter = (id) => { setActiveId(id); setHighlight(false); setView("write"); };

  const [scanLevel, setScanLevel] = useState(() => Number(localStorage.getItem("cs_scan_sensitivity")) || 2);
  const pickScanLevel = (v) => { setScanLevel(v); localStorage.setItem("cs_scan_sensitivity", String(v)); };
  const [detect, setDetect] = useState(null); // {suggestions, source}
  const [detectBusy, setDetectBusy] = useState(false);
  const runScan = async () => {
    setScanning(true);
    try {
      if (saveState !== "saved") await persist();
      const review = project.detail_capture === "review";
      const { data } = await api.post(`/projects/${projectId}/scan`,
        { text: contentRef.current, min_importance: scanLevel, commit: !review });
      refreshCredits?.();
      if (review) {
        const sugg = data.suggestions || [];
        if (!sugg.length) { toast.success("No new details found"); return; }
        setDetect({ source: "scan", suggestions: sugg.map((s, i) => ({ ...s, id: i, keep: s.is_new })) });
        return;
      }
      setEntities(data.entities);
      toast.success(data.added > 0 ? `${data.added} new vital detail(s) detected` : "Codex is up to date — no new details");
      if (data.entities.length) setHighlight(true);
    } catch (e) {
      if (!e.handledOutOfCredits) toast.error(e.response?.data?.detail || "AI scan failed");
    } finally {
      setScanning(false);
    }
  };
  const confirmDetect = async () => {
    const kept = detect.suggestions.filter((s) => s.keep);
    if (!kept.length) { setDetect(null); return; }
    setDetectBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/scan`, { entities: kept });
      setEntities(data.entities);
      setDetect(null);
      toast.success(`${data.added} detail${data.added !== 1 ? "s" : ""} added to your codex`);
      if (data.entities.length) setHighlight(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add details");
    } finally { setDetectBusy(false); }
  };
  const handleImported = (data) => {
    loadAll();
    if (data?.suggestions?.length) {
      setDetect({ source: "import", suggestions: data.suggestions.map((s, i) => ({ ...s, id: i, keep: s.is_new })) });
    }
  };

  const saveEntity = async (form) => {
    const { entity } = entityDialog;
    try {
      if (entity) {
        const { data } = await api.put(`/entities/${entity.id}`, form);
        setEntities((es) => es.map((e) => (e.id === entity.id ? data : e)));
        toast.success("Detail updated across your codex");
      } else {
        const { data } = await api.post(`/projects/${projectId}/entities`, form);
        setEntities((es) => [data, ...es]);
        toast.success("Detail added");
      }
      setEntityDialog({ open: false, entity: null });
    } catch (err) {
      // Keep the dialog open so nothing typed is lost; retry when back online.
      toast.error(err.response?.data?.detail || "Couldn't reach the server — your details are still here, please try again in a moment.");
    }
  };

  const deleteEntity = async (entity) => {
    await api.delete(`/entities/${entity.id}`);
    setEntities((es) => es.filter((e) => e.id !== entity.id));
    setEntityDialog({ open: false, entity: null });
    toast.success("Detail removed");
  };

  const toggleIncluded = async (entity) => {
    const { data } = await api.put(`/entities/${entity.id}`, { included: !entity.included });
    setEntities((es) => es.map((e) => (e.id === entity.id ? data : e)));
  };

  const toggleLoadout = async (entity, value) => {
    const { data } = await api.put(`/entities/${entity.id}`, { loadout_owner: value });
    setEntities((es) => es.map((e) => (e.id === entity.id ? data : e)));
    await reloadInventory();
    toast.success(value ? `${entity.name} will show in Loadouts` : `${entity.name} hidden from Loadouts (still in your Codex)`);
  };

  const toggleKia = async (entity, value) => {
    const { data } = await api.put(`/entities/${entity.id}`, { kia: value });
    setEntities((es) => es.map((e) => (e.id === entity.id ? data : e)));
    await reloadInventory();
    toast.success(value
      ? `${entity.name} marked KIA — frozen and excluded from Gear Sync`
      : `${entity.name} is active again`);
  };

  const resolveConflictKeepMine = async () => { setConflict(null); await persist({ force: true }); };
  const resolveConflictLoadNewer = () => {
    if (!conflict) return;
    contentRef.current = conflict.serverContent;
    editorRef.current?.setHtml(conflict.serverContent);
    baseUatRef.current = conflict.serverUat;
    setChapters((cs) => cs.map((c) => (c.id === activeId ? { ...c, content: conflict.serverContent, updated_at: conflict.serverUat } : c)));
    clearDraft(activeId);
    setSaveState("saved");
    setConflict(null);
    toast.info("Loaded the newer version from your other device");
  };

  const openVersions = async () => {
    if (!activeId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/chapters/${activeId}/versions`);
      setVersions(data);
    } catch { toast.error("Couldn't load version history"); }
  };
  const restoreVersion = async (vid) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${activeId}/restore/${vid}`);
      contentRef.current = data.content || "";
      editorRef.current?.setHtml(data.content || "");
      baseUatRef.current = data.updated_at;
      setChapters((cs) => cs.map((c) => (c.id === activeId ? { ...c, ...data } : c)));
      clearDraft(activeId);
      setSaveState("saved");
      setVersions(null);
      toast.success("Chapter restored — the previous text was saved to history too");
    } catch { toast.error("Restore failed"); }
  };

  const exportFile = async (format, scope, includeActs, opts = {}) => {
    try {
      const params = { format, scope, include_acts: !!includeActs, ...opts };
      if (scope === "chapter") params.chapter_id = activeId;
      if (scope === "act") params.act_id = opts.act_id;
      if (scope === "chapter" && saveState !== "saved") await persist();
      const res = await api.get(`/projects/${projectId}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const actTitle = (project.acts || []).find((x) => x.id === opts.act_id)?.title || "Act";
      const base = scope === "chapter" ? (chapterTitles[activeId] || activeChapter?.title || "chapter")
        : scope === "act" ? `${project.title} - ${actTitle}` : project.title;
      a.download = `${base}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };
  const exportCodex = () => {
    download(`${project.title} - codex.json`, JSON.stringify(entities.map(({ id, name, type, description, detail, included }) => ({ name, type, description, detail, included })), null, 2));
  };

  const [cleaning, setCleaning] = useState(false);
  const applyAiChapter = (data) => {
    const id = activeId;
    contentRef.current = data.content || "";
    editorRef.current?.setHtml(data.content || "");
    baseUatRef.current = data.chapter?.updated_at;
    setChapters((cs) => cs.map((c) => (c.id === id ? { ...c, ...(data.chapter || {}) } : c)));
    clearDraft(id);
    setSaveState("saved");
    refreshCredits?.();
  };

  const PARA_JOBS = {
    gore: { run: gorePassChapter, loading: "Gore pass: making the violent scenes more graphic…", none: "No violent scenes found in this chapter — nothing changed, no credits used", done: (n) => `Gore pass done: ${n} violent passage${n === 1 ? "" : "s"} made more graphic. The old version is in History.`, fail: "Gore pass failed — please try again" },
    tidy: { run: tidyChapter, loading: "Tidying repeated phrases and sentence habits…", none: "This chapter is already within the repetition limits — nothing changed, no credits used", done: (n) => `Tidied ${n} paragraph${n === 1 ? "" : "s"}. The old version is in History.`, fail: "Tidy failed — please try again" },
  };
  const runParaJob = async (kind) => {
    if (!activeId || paraBusy) return;
    const id = activeId;
    const cfg = PARA_JOBS[kind];
    setParaBusy(kind);
    const t = toast.loading(cfg.loading);
    try {
      await persist();
      const data = await cfg.run(projectId, id);
      if (!data.changed) { toast.info(cfg.none, { id: t }); return; }
      if (activeIdRef.current === id) applyAiChapter(data);
      else setChapters((cs) => cs.map((c) => (c.id === id ? { ...c, ...(data.chapter || {}) } : c)));
      toast.success(cfg.done(data.changed), { id: t });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : cfg.fail), { id: t });
    } finally { setParaBusy(""); }
  };

  const cleanFormatting = async () => {
    if (cleaning) return;
    setCleaning(true);
    const t = toast.loading("Cleaning up formatting…");
    try {
      if (saveState !== "saved") await persist();
      const { data } = await api.post(`/projects/${projectId}/clean-formatting`);
      const ch = await api.get(`/projects/${projectId}/chapters`);
      setChapters(ch.data);
      const active = ch.data.find((c) => c.id === activeId);
      if (active) {
        contentRef.current = active.content || "";
        baseUatRef.current = active.updated_at;
        clearDraft(activeId);
        editorRef.current?.setHtml(active.content || "");
        setSaveState("saved");
      }
      toast.success(
        data.cleaned ? `Cleaned formatting in ${data.cleaned} chapter${data.cleaned > 1 ? "s" : ""}` : "Nothing to clean — already tidy",
        { id: t });
    } catch {
      toast.error("Could not clean formatting", { id: t });
    } finally {
      setCleaning(false);
    }
  };

  const onPreviewClick = (e) => {
    const el = e.target.closest("[data-entity-id]");
    if (el) {
      const ent = entities.find((x) => x.id === el.getAttribute("data-entity-id"));
      if (ent) setEntityDialog({ open: true, entity: ent });
    }
  };

  const renderChaptersPanel = (onPick = switchChapter) => (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Chapters</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="add-new-chapter-button" title="Add a chapter or section" className="text-primary hover:scale-110 transition-transform">
              <Plus className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover w-52">
            <DropdownMenuItem onClick={addChapter} data-testid="add-chapter-menu-item">
              <FileText className="w-4 h-4 mr-2" /> New chapter
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">Add a section</DropdownMenuLabel>
            {bookSections.map((s, i) => (
              <DropdownMenuItem key={`${s.title}-${i}`} onClick={() => addSection(s)} data-testid={`add-section-${secSlug(s.title)}`}>
                <FilePlus2 className="w-4 h-4 mr-2 text-muted-foreground" /> {s.title}
                <span className="ml-auto text-[10px] text-muted-foreground">{s.placement === "front" ? "front" : "back"}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setWhatsNextOpen(true)} data-testid="add-section-whatsnext">
              <Sparkles className="w-4 h-4 mr-2 text-primary" /> What's Next (book teaser)
              <span className="ml-auto text-[10px] text-muted-foreground">last</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setManageSections(true)} data-testid="manage-sections-menu-item">
              <Settings2 className="w-4 h-4 mr-2" /> Manage sections…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {chapters.map((c, i) => (
            <div key={c.id} data-testid={`chapter-list-item-${c.id}`}
              className={`group rounded-lg px-3 py-2.5 cursor-pointer transition-colors border
                ${c.id === activeId ? "bg-primary/10 border-primary/40" : "border-transparent hover:bg-secondary/60"}`}
              onClick={() => onPick(c.id)}>
              {renameId === c.id ? (
                <Input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                  onBlur={saveRename} onKeyDown={(e) => e.key === "Enter" && saveRename()}
                  onClick={(e) => e.stopPropagation()} className="h-7 text-sm" data-testid={`rename-input-${c.id}`} />
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{chapterTitles[c.id] || c.title}</div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                      <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS[c.status]?.color || STATUS.draft.color}`}>
                        {STATUS[c.status]?.label || "Draft"}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{(c.word_count || 0).toLocaleString()}w</span>
                      {(c.word_count || 0) > 0 && (
                        <span className="flex items-center gap-2 text-[10px] text-muted-foreground" data-testid={`chapter-times-${c.id}`}>
                          <span className="flex items-center gap-0.5" title="Estimated reading time"><Clock className="w-3 h-3" /> {fmtMins(readMins(c.word_count))}</span>
                          {AUDIOBOOKS_ENABLED && <span className="flex items-center gap-0.5" title="Estimated listening time"><Headphones className="w-3 h-3" /> {fmtMins(listenMins(c.word_count))}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="text-muted-foreground hover:text-foreground" data-testid={`chapter-up-${c.id}`}><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="text-muted-foreground hover:text-foreground" data-testid={`chapter-down-${c.id}`}><ChevronDown className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
              {c.id === activeId && renameId !== c.id && (
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/60">
                  <button onClick={(e) => { e.stopPropagation(); setRenameId(c.id); setRenameVal(chapterTitles[c.id] || c.title); }} className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1" data-testid={`rename-chapter-${c.id}`}><Edit3 className="w-3 h-3" /> Rename</button>
                  <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                    <SelectTrigger className="h-6 text-[11px] px-1.5 w-24 border-none bg-transparent" data-testid={`status-select-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="review">In review</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                  {chapters.length > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); setDeleteChapter(c); }} className="text-[11px] text-muted-foreground hover:text-destructive ml-auto" data-testid={`delete-chapter-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {project?.whats_next?.enabled && (
          <div className="p-2 pt-0">
            <div data-testid="whatsnext-card" onClick={() => setWhatsNextOpen(true)}
              className="rounded-lg px-3 py-2.5 cursor-pointer border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{project.whats_next.heading || "What's Next"}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    Teaser{project.whats_next.title ? ` · ${project.whats_next.title}` : ""} · always last
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </ScrollArea>
      <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
        <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {stats.words.toLocaleString()} words</span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1" title="Reading time"><Clock className="w-3.5 h-3.5" /> ~{stats.minutes} min</span>
          {AUDIOBOOKS_ENABLED && <span className="flex items-center gap-1" title="Listening time"><Headphones className="w-3.5 h-3.5" /> ~{listenMins(stats.words)} min</span>}
        </span>
      </div>
    </>
  );

  const MOBILE_VIEWS = [
    { v: "blueprint", icon: LayoutList, label: "Blueprint", testid: "mobile-view-blueprint" },
    { v: "write", icon: PenSquare, label: "Write", testid: "mobile-view-write" },
    { v: "codex", icon: Library, label: "Codex", testid: "mobile-view-codex" },
    { v: "inventory", icon: Boxes, label: "Loadouts", testid: "mobile-view-inventory" },
    ...(project?.mode === "branching" ? [{ v: "map", icon: Waypoints, label: "Map", testid: "mobile-view-map" }] : []),
  ];

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {panelMode ? (
        <header className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-card/40 sticky top-0 z-30">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <Feather className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold leading-tight truncate">
              {view === "codex" ? "Codex" : view === "inventory" ? "Loadouts" : "Blueprint"}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">{project.title} · synced panel</div>
          </div>
          <button onClick={() => applyRemote()} data-testid="panel-refresh-button" title="Refresh from the main window"
            className="ml-auto text-muted-foreground hover:text-primary flex items-center gap-1.5 text-xs">
            <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Synced</span>
          </button>
        </header>
      ) : (
      <Header back>
        <div className="flex items-center gap-3 min-w-0 w-full">
          <div className="min-w-0">
            {editingTitle ? (
              <input autoFocus value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                data-testid="project-title-edit-input"
                className="font-display text-xl font-bold leading-tight bg-transparent border-b border-primary/60 outline-none w-full max-w-[50vw]" />
            ) : (
              <button onClick={() => { setTitleDraft(project.title); setEditingTitle(true); }}
                data-testid="project-title-edit" title="Rename book"
                className="group/title flex items-center gap-1.5 min-w-0">
                <h1 className="font-display text-lg sm:text-xl font-bold truncate leading-tight">{project.title}</h1>
                <Edit3 className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0" />
              </button>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <SaveIndicator state={saveState} onRetry={flushDrafts} />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <CreditPill />
            <div className="hidden sm:flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
              <ViewBtn active={view === "blueprint"} onClick={() => changeView("blueprint")} icon={LayoutList} label="Blueprint" testid="view-blueprint" />
              <ViewBtn active={view === "write"} onClick={() => changeView("write")} icon={PenSquare} label="Manuscript" testid="view-write" />
              <ViewBtn active={view === "codex"} onClick={() => changeView("codex")} icon={Library} label="Codex" testid="view-codex" count={entities.length} />
              <ViewBtn active={view === "inventory"} onClick={() => changeView("inventory")} icon={Boxes} label="Loadouts" testid="view-inventory" />
              {project.mode === "branching" && (
                <ViewBtn active={view === "map"} onClick={() => changeView("map")} icon={Waypoints} label="Map" testid="view-map" />
              )}
            </div>
            {currentPanelKey && (
              <Button variant="outline" size="sm" onClick={() => undock(currentPanelKey)} data-testid="undock-panel-button"
                title="Pop this panel out into its own window — great for a second monitor" className="gap-1.5 h-8 hidden lg:inline-flex">
                <ExternalLink className="w-4 h-4" /> <span className="hidden xl:inline">Pop out</span>
              </Button>
            )}
            <div className="flex items-center">
              <Button onClick={runScan} disabled={scanning} data-testid="ai-detect-entities-button" title="Detect vital details with AI — costs 2 AI credits" className="gap-2 font-semibold rounded-r-none" size="sm">
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                <span className="hidden md:inline">AI Scan</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="rounded-l-none px-2 border-l border-primary-foreground/25" data-testid="scan-sensitivity-trigger" title="Detail detection sensitivity">
                    <SlidersHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover w-60">
                  <DropdownMenuLabel className="text-xs flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" /> How much should AI Scan detect?</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {[
                    { v: 3, l: "Key details only", d: "Major characters, places & items" },
                    { v: 2, l: "Balanced", d: "Recommended" },
                    { v: 1, l: "Everything", d: "Include minor & background" },
                  ].map((o) => (
                    <DropdownMenuItem key={o.v} onClick={() => pickScanLevel(o.v)} data-testid={`scan-level-${o.v}`} className="flex-col items-start gap-0.5 py-2">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        {scanLevel === o.v ? <Check className="w-3.5 h-3.5 text-primary" /> : <span className="w-3.5 inline-block" />}
                        {o.l}
                      </span>
                      <span className="text-[11px] text-muted-foreground pl-5">{o.d}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs">Before adding to Codex</DropdownMenuLabel>
                  {[
                    { v: "auto", l: "Add automatically", d: "Detected details go straight in" },
                    { v: "review", l: "Review first", d: "You approve each before it's added" },
                  ].map((o) => (
                    <DropdownMenuItem key={o.v} onClick={() => updateProject({ detail_capture: o.v })} data-testid={`capture-mode-${o.v}`} className="flex-col items-start gap-0.5 py-2">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        {(project.detail_capture || "auto") === o.v ? <Check className="w-3.5 h-3.5 text-primary" /> : <span className="w-3.5 inline-block" />}
                        {o.l}
                      </span>
                      <span className="text-[11px] text-muted-foreground pl-5">{o.d}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <ToolMenu {...{ aiOn, setImportOpen, setExportOpen, exportCodex, cleanFormatting, setAudiobookOpen, setElevenReaderOpen, setAutocorrectOpen, setDraftOpen, setRewriteOpen, setPolishOpen, setBriefOpen, hasBrief: !!project?.style_brief, canRewrite: !!activeChapter, runParaJob, paraBusy, setTidyBookOpen, setContinuityOpen, setDedupeOpen, continuityCount: (project?.continuity_report?.findings || []).filter((f) => !f.resolved).length }} />
          </div>
        </div>
      </Header>
      )}

      {view === "map" && project.mode === "branching" ? (
        <BranchMap chapters={chapters} acts={project.acts || []} onOpenChapter={openChapter} />
      ) : view === "blueprint" ? (
        <BlueprintBoard
          project={project} chapters={chapters} entities={entities} api={api} projectId={projectId}
          onUpdateProject={updateProject} onUpdateChapter={updateChapterMeta}
          onReorder={reorderChapters} onAddChapter={addChapterToAct} onCreateSection={createSection}
          onDeleteChapter={setDeleteChapter} onOpenChapter={openChapter} onOpenBeats={openBeats} />
      ) : view === "inventory" ? (
        <InventoryView entities={entities} inventory={inventory} api={api} projectId={projectId}
          customTypes={project.custom_types || []} onAddType={addCustomType}
          onReloadEntities={reloadEntities} onReloadInventory={reloadInventory} />
      ) : view === "codex" ? (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-border flex-wrap">
            <div className="min-w-0">
              <h2 className="font-display text-2xl sm:text-3xl font-bold">Storyboard Codex</h2>
              <p className="text-sm text-muted-foreground hidden sm:block">Every vital detail your story remembers. Search, filter and mark as included.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button onClick={() => setEntityDialog({ open: true, entity: null })} className="gap-2" data-testid="add-entity-button">
                <Plus className="w-4 h-4" /> Add detail
              </Button>
              <Button variant="outline" onClick={() => setTypesOpen(true)} className="gap-2" data-testid="manage-types-button">
                <Plus className="w-4 h-4" /> Types
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden max-w-6xl w-full mx-auto">
            <CodexGrid entities={entities} onEdit={(e) => setEntityDialog({ open: true, entity: e })}
              onToggleIncluded={toggleIncluded} onToggleLoadout={toggleLoadout} onToggleKia={toggleKia} onAdd={() => setEntityDialog({ open: true, entity: null })}
              customTypes={project.custom_types || []} onSuggestVoices={suggestVoices} suggestingVoices={suggestingVoices} />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Chapters sidebar */}
          <aside className="w-64 xl:w-72 hidden md:flex flex-col border-r border-border bg-card/40">
            {renderChaptersPanel()}
          </aside>

          {/* Editor / preview */}
          <main className="flex-1 flex flex-col min-w-0 bg-background">
            <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2 border-b border-border bg-card/30 flex-wrap">
              <button onClick={() => setChaptersSheetOpen(true)} data-testid="mobile-chapters-button"
                className="md:hidden flex items-center gap-1.5 text-sm font-medium min-w-0 max-w-[45vw] text-foreground hover:text-primary transition-colors">
                <Menu className="w-4 h-4 shrink-0" />
                <span className="truncate">{activeChapter ? (chapterTitles[activeChapter.id] || activeChapter.title) : "Chapters"}</span>
              </button>
              {activeChapter && (
                <Button variant="outline" size="sm" onClick={() => openBeats(activeId)} data-testid="open-beats-button" className="gap-1.5 h-8">
                  <ListChecks className="w-4 h-4" /> <span className="hidden sm:inline">Beats</span>
                </Button>
              )}
              {aiOn && activeChapter && (
                <Button variant="outline" size="sm" onClick={runProofread} disabled={proofing} data-testid="proofread-button" title="Proofread this section — costs 2 AI credits" className="gap-1.5 h-8">
                  {proofing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />} <span className="hidden sm:inline">Proofread</span>
                </Button>
              )}
              {activeChapter && (
                <Button variant="outline" size="sm" onClick={openVersions} data-testid="version-history-button" title="View & restore earlier saved versions of this section" className="gap-1.5 h-8">
                  <History className="w-4 h-4" /> <span className="hidden sm:inline">History</span>
                </Button>
              )}
              {AUDIOBOOKS_ENABLED && activeChapter && (
                <NarrationBar projectId={projectId} chapterId={activeId} project={project}
                  projectTitle={project.title} chapterTitle={chapterTitles[activeId] || activeChapter?.title}
                  onVoiceSaved={(vid) => setProject((p) => ({ ...p, narration_voice_id: vid }))} />
              )}
              {activeChapter && (activeChapter.title || "").trim().toLowerCase() === "about the author" && (
                <Button variant="outline" size="sm" onClick={saveAuthorBio} data-testid="save-author-bio-button"
                  title="Save this as your reusable Author Bio — it prefills the About the Author section in your other books"
                  className="gap-1.5 h-8">
                  <BookmarkPlus className="w-4 h-4" /> <span className="hidden sm:inline">Save as my bio</span>
                </Button>
              )}
              {activeChapter && (
                <div className="flex items-center">
                  <Button variant="outline" size="sm" onClick={runGearSync} disabled={gearBusy} data-testid="sync-gear-button" title="Scan this section for gear changes — costs 5 AI credits" className="gap-1.5 h-8 rounded-r-none">
                    {gearBusy && gear?.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Boxes className="w-4 h-4" />} <span className="hidden sm:inline">Sync gear</span>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={gearBusy} data-testid="sync-gear-menu" className="h-8 px-1.5 rounded-l-none border-l-0">
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover">
                      <DropdownMenuItem onClick={runGearSync} data-testid="sync-gear-new">
                        <Boxes className="w-4 h-4 mr-2" /> Sync new changes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={rescanGearFromScratch} data-testid="sync-gear-rescan">
                        <RefreshCw className="w-4 h-4 mr-2" /> Rescan from scratch
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              {activeChapter && entities.some((e) => e.type === "character") && (
                <div className="flex items-center gap-1.5" data-testid="pov-picker"
                  title="Who narrates this chapter? First-person 'I'/'me'/'we' in Sync gear is attributed to them.">
                  <UserRound className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground hidden sm:inline">POV</span>
                  <select
                    value={activeChapter.pov_character_id || ""}
                    onChange={(e) => updateChapterMeta(activeId, { pov_character_id: e.target.value })}
                    data-testid="pov-character-select"
                    className="text-xs bg-secondary/50 border border-border rounded-md px-2 py-1 h-8 max-w-[140px] focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="">Narrator…</option>
                    {entities.filter((e) => e.type === "character").map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer ml-auto">
                <Highlighter className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground hidden sm:inline">Highlight details</span>
                <Switch checked={highlight} onCheckedChange={setHighlight} data-testid="highlight-toggle-switch" />
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <SpellCheck2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground hidden sm:inline">Spellcheck</span>
                <Switch checked={spellcheck} onCheckedChange={setSpellcheck} data-testid="spellcheck-toggle-switch" />
              </label>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
            {!activeChapter ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <BookOpen className="w-12 h-12 text-muted-foreground/40 mb-4" />
                <p className="text-muted-foreground mb-4">No chapters yet.</p>
                <Button onClick={addChapter} className="gap-2"><Plus className="w-4 h-4" /> Add a chapter</Button>
              </div>
            ) : highlight ? (
              <div className="flex-1 overflow-y-auto py-10 lg:py-16 px-4" onClick={onPreviewClick} data-testid="highlight-preview">
                <div className="manuscript-page mx-auto">
                  <div className="flex items-center gap-2 mb-6 text-sm text-primary bg-primary/10 rounded-lg px-3 py-2">
                    <Eye className="w-4 h-4" /> Read-only highlight view — click any highlighted detail to edit it. Turn off to keep writing.
                  </div>
                  <div className="manuscript" dangerouslySetInnerHTML={{ __html: highlightHtml(contentRef.current, entities) }} />
                </div>
              </div>
            ) : (
              <RichEditor key={activeId} ref={editorRef} initialHtml={pickInitialHtml(activeChapter)} spellcheck={spellcheck}
                initialScroll={scrollPos.current[activeId] || 0}
                protectedWords={protectedNames}
                customRules={customRules}
                projectId={projectId} chapterId={activeId} aiTools={aiOn}
                prevChapterId={(() => { const i = chapters.findIndex((c) => c.id === activeId); return i > 0 ? chapters[i - 1].id : null; })()}
                onChange={onEditorChange} onStats={setStats} />
            )}
            </div>

            {project.mode === "branching" && activeChapter && !highlight && (
              <div className="shrink-0 border-t border-border bg-card/40 max-h-[38%] overflow-y-auto px-4 py-4" data-testid="manuscript-choices">
                <div className="mx-auto max-w-3xl">
                  <ChoicesEditor chapter={activeChapter} chapters={chapters}
                    onUpdate={(patch) => updateChapterMeta(activeId, patch)}
                    onCreateSection={createSection} onOpenChapter={openChapter} />
                </div>
              </div>
            )}
          </main>

          {/* Codex panel */}
          <aside className="w-80 xl:w-96 hidden lg:flex flex-col border-l border-border bg-card/40">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Vital details</span>
                <div className="font-display text-lg font-bold leading-none mt-0.5">Story Memory</div>
              </div>
              <button onClick={() => setEntityDialog({ open: true, entity: null })} className="text-primary hover:scale-110 transition-transform" data-testid="panel-add-entity"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 min-h-0">
              <CodexGrid compact entities={entities} onEdit={(e) => setEntityDialog({ open: true, entity: e })}
                onToggleIncluded={toggleIncluded} onToggleLoadout={toggleLoadout} onToggleKia={toggleKia} onAdd={() => setEntityDialog({ open: true, entity: null })}
                customTypes={project.custom_types || []} onSuggestVoices={suggestVoices} suggestingVoices={suggestingVoices}
                onInsert={insertToEditor} />
            </div>
          </aside>
        </div>
      )}

      <nav className="sm:hidden shrink-0 border-t border-border bg-card/80 backdrop-blur-xl flex items-stretch z-30" data-testid="mobile-bottom-nav" style={panelMode ? { display: "none" } : undefined}>
        {MOBILE_VIEWS.map((mv) => {
          const active = view === mv.v;
          const Icon = mv.icon;
          return (
            <button key={mv.v} onClick={() => changeView(mv.v)} data-testid={mv.testid}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>
              <span className="relative">
                <Icon className={`w-5 h-5 ${active ? "scale-110" : ""} transition-transform`} />
                {mv.v === "codex" && entities.length > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 text-[9px] leading-none bg-primary text-primary-foreground px-1 py-0.5 rounded-full">{entities.length}</span>
                )}
              </span>
              {mv.label}
            </button>
          );
        })}
      </nav>

      <Sheet open={chaptersSheetOpen} onOpenChange={setChaptersSheetOpen}>
        <SheetContent side="left" className="p-0 w-[82vw] max-w-xs flex flex-col" data-testid="mobile-chapters-sheet">
          <SheetHeader className="px-4 pt-4">
            <SheetTitle className="font-display text-xl">Chapters</SheetTitle>
          </SheetHeader>
          <div className="flex-1 flex flex-col min-h-0">
            {renderChaptersPanel((id) => { switchChapter(id); setChaptersSheetOpen(false); })}
          </div>
        </SheetContent>
      </Sheet>

      <EntityDialog open={entityDialog.open} entity={entityDialog.entity} customTypes={project.custom_types || []}
        onAddType={addCustomType}
        onOpenChange={(v) => setEntityDialog({ open: v, entity: v ? entityDialog.entity : null })}
        onSave={saveEntity} onDelete={deleteEntity} />

      <ManageTypesDialog open={typesOpen} onOpenChange={setTypesOpen}
        customTypes={project.custom_types || []}
        onSave={(ct) => updateProject({ custom_types: ct })} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={projectId} detailCapture={project.detail_capture || "auto"} onImported={handleImported} />

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} onExport={exportFile}
        chapterTitle={activeChapter?.title} hasCover={!!project?.cover_path} acts={project?.acts || []}
        projectId={projectId} hasChapterImage={!!project?.chapter_image_path}
        onChapterImageChange={(has) => setProject((p) => (p ? { ...p, chapter_image_path: has ? "set" : null } : p))} />
      <AutocorrectDialog open={autocorrectOpen} onOpenChange={setAutocorrectOpen}
        onSaved={(list) => setCustomRules(rulesToMap(list))} />
      <WhatsNextDialog open={whatsNextOpen} onOpenChange={setWhatsNextOpen}
        currentProjectId={projectId} value={project?.whats_next}
        onSave={(wn) => updateProject({ whats_next: wn })} />

      {AUDIOBOOKS_ENABLED && (
        <AudiobookDialog open={audiobookOpen} onOpenChange={setAudiobookOpen}
          projectId={projectId} projectTitle={project.title} />
      )}

      <ElevenReaderDialog open={elevenReaderOpen} onOpenChange={setElevenReaderOpen}
        onDownloadEpub={() => exportFile("epub", "project", false)} />

      <DraftGeneratorDialog open={draftOpen} onOpenChange={setDraftOpen}
        projectId={projectId} project={project}
        onProjectPatch={(patch) => setProject((p) => ({ ...p, ...patch }))}
        onDone={async () => {
          try {
            const [p, ch] = await Promise.all([api.get(`/projects/${projectId}`), api.get(`/projects/${projectId}/chapters`)]);
            setProject(p.data); setChapters(ch.data); refreshCredits?.();
          } catch { /* ignore */ }
        }} />

      <RewriteChapterDialog open={rewriteOpen} onOpenChange={setRewriteOpen}
        projectId={projectId} chapter={activeChapter}
        onDone={applyAiChapter} />
      <PolishChapterDialog open={polishOpen} onOpenChange={setPolishOpen} projectId={projectId} chapter={activeChapter}
        hasBrief={!!project?.style_brief} onBeforeStart={persist} onDone={applyAiChapter}
        onOpenBrief={() => { setPolishOpen(false); setBriefOpen(true); }} />
      <StyleBriefDialog open={briefOpen} onOpenChange={setBriefOpen} project={project} onSave={updateProject} />
      <TidyBookDialog open={tidyBookOpen} onOpenChange={setTidyBookOpen} projectId={projectId}
        onBeforeStart={persist} onDone={() => { syncFromServer(); refreshCredits?.(); }} />

      <ContinuityDialog open={continuityOpen} onOpenChange={setContinuityOpen}
        projectId={projectId}
        seriesOrder={project?.series_order || 0}
        onJump={(id) => { switchChapter(id); setContinuityOpen(false); }}
        onChanged={async () => {
          try {
            const [ch, p] = await Promise.all([api.get(`/projects/${projectId}/chapters`), api.get(`/projects/${projectId}`)]);
            setChapters(ch.data); setProject(p.data);
          } catch { /* ignore */ }
          refreshCredits?.();
        }} />

      <DedupeDialog open={dedupeOpen} onOpenChange={setDedupeOpen}
        projectId={projectId}
        onJump={(id) => { switchChapter(id); setDedupeOpen(false); }}
        onChanged={async () => {
          try {
            const [ch, p] = await Promise.all([api.get(`/projects/${projectId}/chapters`), api.get(`/projects/${projectId}`)]);
            setChapters(ch.data); setProject(p.data);
          } catch { /* ignore */ }
          refreshCredits?.();
        }} />

      <ProofreadDialog proof={proof} setProof={setProof} onApply={applyFix} onFixAll={fixAll} />


      <GearSyncDialog gear={gear} setGear={setGear} onApply={applyGearSync} busy={gearBusy}
        sources={gear?.sources || []}
        onMarkKia={markKiaFromSuggestion} onDismissKia={dismissKiaSuggestion}
        owners={entities.filter((e) => e.type === "character" || e.type === "vehicle").map((e) => ({ id: e.id, name: e.name }))}
        characters={entities.filter((e) => e.type === "character")} />
      <DetectReviewDialog detect={detect} setDetect={setDetect} onConfirm={confirmDetect} busy={detectBusy} />

      <ManageSectionsDialog open={manageSections} onOpenChange={setManageSections}
        sections={bookSections} onSaveBook={saveBookSections} onSaveDefault={saveDefaultSections} />

      <AlertDialog open={!!conflict} onOpenChange={(v) => !v && setConflict(null)}>
        <AlertDialogContent className="bg-popover" data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>This section changed on another device</AlertDialogTitle>
            <AlertDialogDescription>
              A newer version of this section was saved somewhere else (probably your phone or another tab).
              Choose which one to keep — nothing is deleted, and whichever you don't pick is saved to History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <Button variant="outline" onClick={resolveConflictLoadNewer} data-testid="conflict-load-newer">
              Load the newer version
            </Button>
            <AlertDialogAction onClick={resolveConflictKeepMine} data-testid="conflict-keep-mine">
              Keep what's on this screen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={versions !== null} onOpenChange={(v) => !v && setVersions(null)}>
        <DialogContent className="bg-popover max-w-lg" data-testid="version-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <History className="w-5 h-5 text-primary" /> Version history
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {(versions || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No earlier versions saved yet. The last 20 saves of this section will appear here as you write.
              </p>
            ) : (versions || []).map((v) => (
              <div key={v.id} data-testid={`version-${v.id}`}
                className="rounded-xl border border-border bg-card p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{new Date(v.saved_at).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{v.word_count} words</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{v.preview}</p>
                </div>
                <Button size="sm" variant="outline" className="gap-1.5 shrink-0" data-testid={`restore-version-${v.id}`}
                  onClick={() => restoreVersion(v.id)}>
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>


      <BeatPlanner open={beatOpen} onOpenChange={setBeatOpen}
        chapter={chapters.find((c) => c.id === beatId)}
        chapters={chapters}
        act={(() => { const c = chapters.find((x) => x.id === beatId); return c ? (project.acts || []).find((a) => a.id === c.act_id) : null; })()}
        onUpdate={(patch) => updateChapterMeta(beatId, patch)}
        api={api} projectId={projectId} logline={project.logline}
        onWrite={(id) => { setBeatOpen(false); openChapter(id); }} />

      <AlertDialog open={!!deleteChapter} onOpenChange={(v) => !v && setDeleteChapter(null)}>
        <AlertDialogContent className="bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteChapter ? (chapterTitles[deleteChapter.id] || deleteChapter.title) : ""}"?</AlertDialogTitle>
            <AlertDialogDescription>This chapter and its text will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeChapter} data-testid="confirm-delete-chapter" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SaveIndicator({ state, onRetry }) {
  if (state === "saving") return <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>;
  if (state === "retrying") return (
    <button type="button" onClick={onRetry} data-testid="save-indicator-retrying"
      title="Your work is saved on this device. Click to retry saving to the server now."
      className="flex items-center gap-1 text-amber-500 hover:text-amber-400 transition-colors">
      <Loader2 className="w-3 h-3 animate-spin" /> Reconnecting — saved on this device <span className="underline">Retry</span>
    </button>
  );
  if (state === "dirty") return <span className="flex items-center gap-1 text-amber-500">Unsaved changes</span>;
  return <span className="flex items-center gap-1 text-emerald-500" data-testid="save-indicator-saved"><Check className="w-3 h-3" /> All changes saved</span>;
}

function ViewBtn({ active, onClick, icon: Icon, label, testid, count }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
        ${active ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
      <Icon className="w-4 h-4" /> {label}
      {count > 0 && <span className="text-[10px] bg-primary/15 text-primary px-1.5 rounded-full">{count}</span>}
    </button>
  );
}

function ToolMenu({ aiOn = true, setImportOpen, setExportOpen, exportCodex, cleanFormatting, setAudiobookOpen, setElevenReaderOpen, setAutocorrectOpen, setDraftOpen, setRewriteOpen, setPolishOpen, setBriefOpen, hasBrief, canRewrite, runParaJob, paraBusy, setTidyBookOpen, setContinuityOpen, setDedupeOpen, continuityCount = 0 }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="tools-menu" className="gap-1.5 relative">
          <Download className="w-4 h-4" /> <span className="hidden md:inline">Tools</span>
          {aiOn && continuityCount > 0 && (
            <span data-testid="continuity-badge"
              className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-amber-500 text-[10px] font-bold text-black flex items-center justify-center">
              {continuityCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover w-56">
        {aiOn && <>
        <DropdownMenuItem onClick={() => setDraftOpen(true)} data-testid="menu-generate-draft"><Wand2 className="w-4 h-4 mr-2" /> Generate draft (AI)…</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setRewriteOpen(true)} disabled={!canRewrite} data-testid="menu-rewrite-chapter"><RefreshCw className="w-4 h-4 mr-2" /> Rewrite this chapter (AI)…</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setPolishOpen(true)} disabled={!canRewrite} data-testid="menu-polish-chapter"><Sparkles className="w-4 h-4 mr-2" /> Polish with GPT…</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setBriefOpen(true)} data-testid="menu-style-brief"><Feather className="w-4 h-4 mr-2" /> Style &amp; story brief{hasBrief ? " ✓" : "…"}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => runParaJob("gore")} disabled={!canRewrite || !!paraBusy} data-testid="menu-gore-pass"><Skull className="w-4 h-4 mr-2" /> {paraBusy === "gore" ? "Gore pass running…" : "Make violence more graphic (2 credits)"}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => runParaJob("tidy")} disabled={!canRewrite || !!paraBusy} data-testid="menu-tidy-chapter"><Eraser className="w-4 h-4 mr-2" /> {paraBusy === "tidy" ? "Tidying…" : "Tidy repetition in this chapter (2 credits)"}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTidyBookOpen(true)} data-testid="menu-tidy-book"><Eraser className="w-4 h-4 mr-2" /> Tidy repetition across the whole book…</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setContinuityOpen(true)} data-testid="menu-continuity-check">
          <ShieldCheck className="w-4 h-4 mr-2" /> Continuity check (AI)…
          {continuityCount > 0 && <span className="ml-auto text-[10px] font-bold text-amber-500">{continuityCount}</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setDedupeOpen(true)} data-testid="menu-dedupe-chapters">
          <CopyCheck className="w-4 h-4 mr-2" /> Find &amp; fix repeated chapters (AI)…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        </>}
        <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="menu-import"><Upload className="w-4 h-4 mr-2" /> Import manuscript</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setExportOpen(true)} data-testid="menu-export"><Download className="w-4 h-4 mr-2" /> Export (Word / PDF / Kindle)…</DropdownMenuItem>
        {AUDIOBOOKS_ENABLED && <DropdownMenuItem onClick={() => setAudiobookOpen(true)} data-testid="menu-audiobook"><Headphones className="w-4 h-4 mr-2" /> Full audiobook…</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => setElevenReaderOpen(true)} data-testid="menu-elevenreader"><Headphones className="w-4 h-4 mr-2" /> Listen on ElevenReader…</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={cleanFormatting} data-testid="menu-clean-formatting"><Eraser className="w-4 h-4 mr-2" /> Clean up formatting</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setAutocorrectOpen(true)} data-testid="menu-autocorrect"><Wand2 className="w-4 h-4 mr-2" /> My autocorrect…</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={exportCodex} data-testid="export-codex-button"><Library className="w-4 h-4 mr-2" /> Codex (.json)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ExportDialog({ open, onOpenChange, onExport, chapterTitle, hasCover, projectId, hasChapterImage, onChapterImageChange, acts = [] }) {
  const [format, setFormat] = useState("docx");
  const [scope, setScope] = useState("project");
  const [includeActs, setIncludeActs] = useState(false);
  const [style, setStyle] = useState("book");
  const [pageSize, setPageSize] = useState("a4");
  const [titlePage, setTitlePage] = useState(true);
  const [copyrightPage, setCopyrightPage] = useState(true);
  const [cover, setCover] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const imgInputRef = useRef(null);
  const [actId, setActId] = useState("");
  const wholeBook = scope !== "chapter";
  const actScope = scope === "act";
  const chooseScope = (v) => {
    setScope(v);
    const book = v === "project";
    setTitlePage(book); setCopyrightPage(book); if (!book) setCover(false);
    if (v === "act" && !actId && acts[0]) setActId(acts[0].id);
  };

  const uploadFlourish = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${projectId}/chapter-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Chapter flourish added");
      onChapterImageChange?.(true);
    } catch { toast.error("Couldn't upload that image"); }
    finally { setImgBusy(false); if (imgInputRef.current) imgInputRef.current.value = ""; }
  };
  const removeFlourish = async () => {
    setImgBusy(true);
    try { await api.delete(`/projects/${projectId}/chapter-image`); toast.success("Chapter flourish removed"); onChapterImageChange?.(false); }
    catch { toast.error("Couldn't remove the image"); }
    finally { setImgBusy(false); }
  };

  const pill = (active) => `flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-h-[88vh] overflow-y-auto" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Download className="w-5 h-5 text-primary" /> Export manuscript</DialogTitle>
          <DialogDescription>Book-quality formatting — your bold, italics and headings are preserved. The logline is not included.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Format</Label>
            <div className="flex gap-2 mt-2">
              {[["docx", "Word (.docx)"], ["pdf", "PDF (.pdf)"], ["epub", "Kindle (.epub)"]].map(([v, l]) => (
                <button key={v} onClick={() => setFormat(v)} data-testid={`export-format-${v}`} className={pill(format === v)}>{l}</button>
              ))}
            </div>
            {format === "epub" && (
              <p className="text-xs text-muted-foreground mt-1.5">Reflowable e-book with a chapter table-of-contents. Download it, then use Amazon "Send to Kindle" to read it on your device.</p>
            )}
          </div>

          {format !== "epub" && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Style</Label>
            <div className="flex gap-2 mt-2">
              {[["book", "Book (print-ready)"], ["manuscript", "Manuscript (double-spaced)"]].map(([v, l]) => (
                <button key={v} onClick={() => setStyle(v)} data-testid={`export-style-${v}`} className={pill(style === v)}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {style === "book" ? "Serif, justified, indented paragraphs — reads like a finished book." : "Double-spaced, Times, indented — the standard format for submitting to agents/publishers."}
            </p>
          </div>
          )}

          {format !== "epub" && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Page size</Label>
            <div className="flex gap-2 mt-2">
              {[["a4", "A4"], ["letter", "US Letter"], ["6x9", '6×9" paperback']].map(([v, l]) => (
                <button key={v} onClick={() => setPageSize(v)} data-testid={`export-size-${v}`} className={pill(pageSize === v)}>{l}</button>
              ))}
            </div>
          </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">What to export</Label>
            <div className="flex gap-2 mt-2">
              {[["project", "Whole project"], ...(acts.length ? [["act", "Single act"]] : []), ["chapter", "This chapter"]].map(([v, l]) => (
                <button key={v} onClick={() => chooseScope(v)} data-testid={`export-scope-${v}`} className={pill(scope === v)}>
                  {v === "chapter" && chapterTitle ? "This chapter" : l}</button>
              ))}
            </div>
            {actScope && (
              <Select value={actId} onValueChange={setActId}>
                <SelectTrigger className="mt-2" data-testid="export-act-select"><SelectValue placeholder="Choose an act" /></SelectTrigger>
                <SelectContent>
                  {acts.map((a, i) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`export-act-option-${a.id}`}>{a.title || `Act ${i + 1}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {actScope && <p className="text-xs text-muted-foreground mt-1.5">Exports the act's title page and its chapters, keeping the book's chapter numbering.</p>}
          </div>

          {wholeBook && (
            <div className="space-y-2.5 rounded-lg border border-border p-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Front matter</Label>
              <div className="flex items-center justify-between">
                <Label className="cursor-pointer font-normal">Title page</Label>
                <Switch checked={titlePage} onCheckedChange={setTitlePage} data-testid="export-title-page" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="cursor-pointer font-normal">Copyright page</Label>
                <Switch checked={copyrightPage} onCheckedChange={setCopyrightPage} data-testid="export-copyright-page" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className={`font-normal ${hasCover ? "cursor-pointer" : "text-muted-foreground"}`}>Cover on title page</Label>
                  {!hasCover && <p className="text-xs text-muted-foreground">Upload a cover on the dashboard to enable.</p>}
                </div>
                <Switch checked={cover && hasCover} disabled={!hasCover || !titlePage} onCheckedChange={setCover} data-testid="export-cover" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-normal">Chapter flourish</Label>
                  <p className="text-xs text-muted-foreground">{hasChapterImage ? "A small image shows centred under every chapter title." : "Add a small centred image under every chapter title."}</p>
                </div>
                <div className="flex items-center gap-2">
                  <input ref={imgInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadFlourish} data-testid="chapter-image-input" />
                  {hasChapterImage && (
                    <Button type="button" variant="ghost" size="sm" onClick={removeFlourish} disabled={imgBusy} data-testid="chapter-image-remove" className="text-xs text-muted-foreground">Remove</Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => imgInputRef.current?.click()} disabled={imgBusy || !projectId} data-testid="chapter-image-upload" className="gap-1.5">
                    {imgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}{hasChapterImage ? "Change" : "Upload"}
                  </Button>
                </div>
              </div>
              {!actScope && <div className="flex items-center justify-between pt-1 border-t border-border">
                <div>
                  <Label className="cursor-pointer font-normal">Act divider pages</Label>
                  <p className="text-xs text-muted-foreground">A full page for each act (e.g. "Act I") before its chapters.</p>
                </div>
                <Switch checked={includeActs} onCheckedChange={setIncludeActs} data-testid="export-include-acts" />
              </div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button disabled={actScope && !actId} onClick={() => { onExport(format, scope, actScope || includeActs, { style, page_size: pageSize, title_page: titlePage, copyright_page: copyrightPage, cover: cover && hasCover, ...(actScope ? { act_id: actId } : {}) }); onOpenChange(false); }} data-testid="do-export-button" className="gap-2">
            <Download className="w-4 h-4" /> Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PROOF_COLORS = {
  spelling: "text-red-500 bg-red-500/10",
  grammar: "text-amber-600 dark:text-amber-300 bg-amber-500/10",
  punctuation: "text-sky-500 bg-sky-500/10",
  style: "text-violet-500 bg-violet-500/10",
};

function ProofreadDialog({ proof, setProof, onApply, onFixAll }) {
  const remaining = (proof?.issues || []).filter((i) => !i.done).length;
  return (
    <Dialog open={!!proof} onOpenChange={(v) => !v && setProof(null)}>
      <DialogContent className="bg-popover max-w-lg" data-testid="proofread-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CheckCheck className="w-5 h-5 text-primary" /> Proofread
          </DialogTitle>
          <DialogDescription>
            Spelling & grammar suggestions for this chapter. Apply the ones you like.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[52vh] overflow-y-auto space-y-2 py-1">
          {proof?.loading ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Loader2 className="w-7 h-7 animate-spin text-primary mb-3" /> Reading your prose…
            </div>
          ) : (proof?.issues || []).map((issue) => (
            <div key={issue.id} data-testid={`proof-issue-${issue.id}`}
              className={`rounded-lg border p-3 ${issue.done ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${PROOF_COLORS[issue.type] || PROOF_COLORS.grammar}`}>{issue.type}</span>
                {issue.done ? (
                  <span className="text-xs text-emerald-500 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Fixed</span>
                ) : (
                  <Button size="sm" onClick={() => onApply(issue)} data-testid={`proof-apply-${issue.id}`} className="h-7">Apply fix</Button>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-14 shrink-0 pt-0.5">Change</span>
                  <span className="rounded bg-red-500/10 text-red-500 dark:text-red-300 px-1.5 py-0.5 line-through decoration-red-500/60 break-words">{issue.original}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-14 shrink-0 pt-0.5">To</span>
                  <span className="rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 px-1.5 py-0.5 font-semibold break-words">{issue.suggestion}</span>
                </div>
              </div>
              {issue.note && <p className="text-xs text-muted-foreground mt-2 pl-16">{issue.note}</p>}
            </div>
          ))}
          {!proof?.loading && remaining === 0 && (proof?.issues || []).length > 0 && (
            <p className="text-center text-sm text-emerald-500 py-3">All fixes applied ✓</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setProof(null)}>Close</Button>
          <Button onClick={onFixAll} disabled={remaining === 0} data-testid="proof-fix-all-button" className="gap-2">
            <CheckCheck className="w-4 h-4" /> Fix all ({remaining})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ManageSectionsDialog({ open, onOpenChange, sections, onSaveBook, onSaveDefault }) {
  const [list, setList] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setList((sections || []).map((s) => ({ title: s.title, placement: s.placement === "front" ? "front" : "back" })));
  }, [open, sections]);

  const add = () => {
    const t = newTitle.trim();
    if (!t) return;
    if (list.some((s) => s.title.toLowerCase() === t.toLowerCase())) { toast.info("That section is already in the list"); return; }
    setList((l) => [...l, { title: t, placement: "back" }]);
    setNewTitle("");
  };
  const remove = (i) => setList((l) => l.filter((_, idx) => idx !== i));
  const setPlacement = (i, p) => setList((l) => l.map((s, idx) => (idx === i ? { ...s, placement: p } : s)));

  const saveBook = async () => { setBusy(true); try { await onSaveBook(list); onOpenChange(false); } finally { setBusy(false); } };
  const saveDefault = async () => { setBusy(true); try { await onSaveDefault(list); } finally { setBusy(false); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-md max-h-[90vh] overflow-y-auto" data-testid="manage-sections-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Settings2 className="w-5 h-5 text-primary" /> Manage sections</DialogTitle>
          <DialogDescription>These appear in the "+" menu for this book. Front-matter goes before Chapter 1; back-matter after the last chapter. Sections are never auto-numbered.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          {list.map((s, i) => (
            <div key={`${s.title}-${i}`} data-testid={`manage-section-row-${secSlug(s.title)}`}
              className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-2.5 py-1.5">
              <span className="flex-1 min-w-0 truncate text-sm font-medium">{s.title}</span>
              <div className="flex rounded-md border border-border overflow-hidden shrink-0">
                <button onClick={() => setPlacement(i, "front")} data-testid={`section-front-${secSlug(s.title)}`}
                  className={`px-2 py-1 text-[11px] flex items-center gap-1 ${s.placement === "front" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
                  <ArrowUpToLine className="w-3 h-3" /> Front
                </button>
                <button onClick={() => setPlacement(i, "back")} data-testid={`section-back-${secSlug(s.title)}`}
                  className={`px-2 py-1 text-[11px] flex items-center gap-1 border-l border-border ${s.placement === "back" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
                  <ArrowDownToLine className="w-3 h-3" /> Back
                </button>
              </div>
              <button onClick={() => remove(i)} data-testid={`remove-section-${secSlug(s.title)}`}
                className="text-muted-foreground hover:text-destructive shrink-0"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No sections yet — add one below.</p>}
          <div className="flex items-center gap-2 pt-1">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="Add a section (e.g. Content Warning)" data-testid="new-section-input" />
            <Button variant="outline" onClick={add} disabled={!newTitle.trim()} data-testid="add-section-to-list" className="shrink-0 gap-1.5">
              <Plus className="w-4 h-4" /> Add
            </Button>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={saveDefault} disabled={busy} data-testid="save-sections-default" className="gap-1.5">
            <BookmarkPlus className="w-4 h-4" /> Save as my default
          </Button>
          <Button onClick={saveBook} disabled={busy} data-testid="save-sections-book" className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save for this book
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
